import axios from "axios";
import { useState, useEffect } from "react";

import { Resource} from "../types";

interface ApiResponse {
  data?: any[]; // raw courses array from API
  total?: number;
  detail?: string;
}

export interface FilterState {
  selectedSubjects: string[];
  selectedDifficulty: string[];
  selectedGrades: string[];
  selectedType: string | null;
  searchQuery: string;
  page: number;
  rowsPerPage: number;
}

export interface FilterHandlers {
  handleSubjectsChange: (subjects: string[]) => void;
  handleDifficultyChange: (difficulty: string[]) => void;
  handleGradesChange: (grades: string[]) => void;
  handleTypeChange: (type: string | null) => void;
  handleSearchChange: (query: string) => void;
  handleResetFilters: () => void;
  handlePageChange: (page: number) => void;
}

export const useFilters = () => {
  const [filters, setFilters] = useState<FilterState>({
    selectedSubjects: [],
    selectedDifficulty: [],
    selectedGrades: [],
    selectedType: null,
    searchQuery: "",
    page: 1,
    rowsPerPage: 5,
  });

  const [resources, setResources] = useState<Resource[]>([]);
  const [filteredResources, setFilteredResources] = useState<Resource[]>([]);
  const [totalOpportunities, setTotalOpportunities] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // eslint-disable-next-line import/no-named-as-default-member
    const source = axios.CancelToken.source();

    const fetchResources = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiUrl = process.env.REACT_APP_API_URL || "https://daha.linkpc.net";

        // Prepare filter params
        const filterParams: Record<string, string | undefined> = {
          subjects:
            filters.selectedSubjects.length > 0
              ? filters.selectedSubjects.join(",")
              : undefined,
          difficulty:
            filters.selectedDifficulty.length > 0
              ? filters.selectedDifficulty.join(",")
              : undefined,
          grades:
            filters.selectedGrades.length > 0
              ? filters.selectedGrades.join(",")
              : undefined,
          type: filters.selectedType || undefined,
          search: filters.searchQuery || undefined,
        };

        // 1. Fetch total count with filters
        const countResponse = await axios.get<{ total: number }>(
          `${apiUrl}/api/courses/count`,
          {
            params: filterParams,
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            cancelToken: source.token,
          }
        );

        const total = countResponse.data.total ?? 0;
        setTotalOpportunities(total);
        console.log(`Total courses reported by API: ${total}`);

        if (total === 0) {
          setResources([]);
          setFilteredResources([]);
          return;
        }

        // 2. Fetch all courses with filters
        const limit = 100; // Backend fetch limit (larger than rowsPerPage)
        const totalPages = Math.ceil(total / limit);
        const allCourses: any[] = [];

        // Delay to avoid rate-limiting
        const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

        for (let page = 0; page < totalPages; page++) {
          const skip = page * limit;
          try {
            console.log(`Fetching page ${page + 1} (skip=${skip}, limit=${limit})`);
            const coursesResponse = await axios.get<ApiResponse>(
              `${apiUrl}/api/courses`,
              {
                params: {
                  limit,
                  skip,
                  ...filterParams,
                },
                headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json",
                },
                cancelToken: source.token,
              }
            );

            console.log(`Raw response for page ${page + 1}:`, coursesResponse.data);

            const responseData = Array.isArray(coursesResponse.data)
              ? coursesResponse.data
              : coursesResponse.data?.data || [];

            if (!Array.isArray(responseData)) {
              console.warn(`Page ${page + 1} returned invalid data:`, coursesResponse.data);
              continue;
            }
            if (responseData.length === 0) {
              console.log(`Page ${page + 1} is empty, stopping fetch`);
              break;
            }
            console.log(`Page ${page + 1} returned ${responseData.length} courses`);

            allCourses.push(...responseData);
            await delay(500); // 500ms delay to avoid rate-limiting
          } catch (err: any) {
            console.warn(`Error fetching page ${page + 1}:`, err);
            continue; // Skip failed page
          }
        }

        // Map to Resource model
        const mappedResources: Resource[] = allCourses
          .filter((item: any): item is any => !!item.id && !!item.title)
          .map((item: any) => ({
            id: item.id,
            title: item.title,
            description: item.description || "",
            url: item.url || "#",
            provider: item.provider || "Неизвестный провайдер",
            level: item.level || "Не указан",
            created_at: item.created_at || new Date().toISOString(),
            updated_at: item.updated_at || new Date().toISOString(),
            category: item.category || { id: 0, name: "Неизвестно" },
            grades: Array.isArray(item.grades) ? item.grades : [],
            startDate: item.start_date || item.created_at || new Date().toISOString(),
            endDate: item.end_date || item.updated_at || new Date().toISOString(),
            gradesEnum: Array.isArray(item.grades)
              ? item.grades.map((g: any) => String(g.level || g))
              : [],
            subjectEnum: item.category?.name || "Неизвестно",
          }));

        console.log(`Total courses fetched: ${mappedResources.length}`);
        setResources(mappedResources);
        setFilteredResources(mappedResources);
      } catch (err: any) {
        console.error("Fetch Resources Error:", err);
        setError(
          err.response?.data?.detail || err.message || "Не удалось загрузить курсы"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchResources();

    return () => {
      source.cancel("Component unmounted");
    };
  }, [
    filters.selectedSubjects,
    filters.selectedDifficulty,
    filters.selectedGrades,
    filters.selectedType,
    filters.searchQuery,
  ]);

  // Client-side filtering (optional, kept for extra filtering)
  useEffect(() => {
    if (loading || error) return;

    const filtered = resources.filter((resource) => {
      if (
        filters.selectedSubjects.length > 0 &&
        !filters.selectedSubjects.includes(resource.category?.name)
      ) {
        return false;
      }

      if (
        filters.selectedDifficulty.length > 0 &&
        !filters.selectedDifficulty.includes(resource.level)
      ) {
        return false;
      }

      if (filters.selectedGrades.length > 0) {
        const resourceGrades = resource.gradesEnum || [];
        if (!resourceGrades.some((grade) => filters.selectedGrades.includes(grade))) {
          return false;
        }
      }

      if (
        filters.searchQuery &&
        !resource.title.toLowerCase().includes(filters.searchQuery.toLowerCase()) &&
        !resource.description.toLowerCase().includes(filters.searchQuery.toLowerCase())
      ) {
        return false;
      }

      return true;
    });

    setFilteredResources(filtered);
  }, [filters, resources, loading, error]);

  // Filter handlers
  const updateFilters = (updates: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...updates, page: 1 }));
  };

  const handlers: FilterHandlers = {
    handleSubjectsChange: (subjects) => updateFilters({ selectedSubjects: subjects }),
    handleDifficultyChange: (difficulty) => updateFilters({ selectedDifficulty: difficulty }),
    handleGradesChange: (grades) => updateFilters({ selectedGrades: grades }),
    handleTypeChange: (type) => updateFilters({ selectedType: type }),
    handleSearchChange: (query) => updateFilters({ searchQuery: query }),
    handleResetFilters: () =>
      updateFilters({
        selectedSubjects: [],
        selectedDifficulty: [],
        selectedGrades: [],
        selectedType: null,
        searchQuery: "",
      }),
    handlePageChange: (page) => setFilters((prev) => ({ ...prev, page })),
  };

  return {
    filters,
    handlers,
    filteredResources,
    totalOpportunities,
    loading,
    error,
  };
};
